import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CorrespondenceAddress } from '@domgen/common-person-types';
import { CONFIG } from '@domgen/dgx-fe-config';
import {
  AnalyticsService,
  LoaderComponent,
  TextSearchComponent,
} from '@domgen/dgx-components';
import { finalize, switchMap } from 'rxjs/operators';

import { AppliancesService } from '../../../services/appliances.service';
import { ManageMyRepairService } from '../../../services/manage-my-repair.service';
import {
  ManageMyRepairResponse,
  MMRItem,
} from '../../../types/manage-my-repair-types';
import { PlanDocumentKeyResponse } from '../../../types/plan-document.model';
import { User } from '../../../types/user.model';
import { UnavailableDocumentsComponent } from '../../../manage-my-repair/components/non-active-repair-state/plan-docs/unavailable-docs.component';
import {
  ProductTypeQuestionState,
  ProductTypeSelectionChange,
} from './product-type-question/product-type-question.component';
import {
  GAS_EMERGENCY_REASONS,
  GAS_SAFETY_ANSWERS,
  GasSafetyAnswer,
} from './product-type-question/product-type-question.constants';
import {
  createDefaultManufacturerState,
  createDefaultProductTypeState,
  createNeedARepairViewModel,
  ManufacturerStepState,
  NeedARepairViewModel,
} from './need-a-repair.view-model';
import posthog from 'posthog-js';

@Component({
  selector: 'claims-repair-need-a-repair',
  templateUrl: './need-a-repair.component.html',
  styleUrls: ['./need-a-repair.component.scss'],
})
export class NeedARepairComponent implements OnInit, OnChanges {
  @Input() claimType: 'REPAIR' | 'SERVICE';
  @Input() mmrItem: MMRItem | null;
  @Input() claim: ManageMyRepairResponse | null;
  @Input() correspondenceAddress: CorrespondenceAddress | null;
  @Input() inWarranty = false;
  @Input() planInWarrantyContactName = '';
  @Input() planInWarrantyContactTel: string | null = null;
  @Input() planInWaitPeriod = false;
  @Output() bookRepairClicked = new EventEmitter<void>();
  @Output() createClaim = new EventEmitter<void>();
  @Output() manufacturerSelected = new EventEmitter<string>();

  private readonly fallbackWarrantyContactTel = '0712 345 6789';
  private _claimTypes: any[] | undefined;

  @Input()
  set claimTypes(claimTypes: any[] | undefined) {
    claimTypes = (claimTypes ?? []).filter((i) => {
      if (i.claimTypeCode === 'AD') {
        if (i.isCurrentlyAvailable === false) {
          return false;
        }
      }
      return true;
    });

    this._claimTypes = claimTypes;
    this.currentClaimTypes = claimTypes;

    if (this.currentClaimTypes.length === 1) {
      const [onlyOption] = this.currentClaimTypes;
      const hasDifferentSelection =
        !this.selectedOption ||
        this.selectedOption?.claimTypeCode !== onlyOption?.claimTypeCode;

      if (hasDifferentSelection) {
        this.applySingleClaimSelection(onlyOption);
      }
    }
  }

  get claimTypes(): any[] | undefined {
    return this._claimTypes;
  }

  applianceInGoodCondition: boolean | undefined;
  receivedBaxiLetter: boolean | undefined;
  currentClaimTypes: any[] = [];
  selectedOption: any;
  pastFirstStage = false;
  addressCorrect: boolean | undefined;
  showAddressConfirmation = false;
  productTypeQuestionState: ProductTypeQuestionState =
    createDefaultProductTypeState();
  resetProductTypeQuestionToggle = false;
  manufacturerState: ManufacturerStepState = createDefaultManufacturerState();
  @ViewChild(TextSearchComponent)
  private readonly manufacturerTextSearch?: TextSearchComponent;

  get viewModel(): NeedARepairViewModel {
    return createNeedARepairViewModel({
      currentClaimTypes: this.currentClaimTypes,
      selectedOption: this.selectedOption ?? null,
      pastFirstStage: this.pastFirstStage,
      claimType: this.claimType,
      manufacturerName: this.manufacturerState.selectedManufacturer,
      applianceInGoodCondition: this.applianceInGoodCondition,
      receivedBaxiLetter: this.receivedBaxiLetter,
      addressCorrect: this.addressCorrect,
      productTypeState: this.productTypeQuestionState,
      hasProductTypeOptions: !!this.claim?.productTypeQuestion?.options?.length,
      isGasPlan: !!this.claim?.isGas,
      manufacturerState: this.manufacturerState,
    });
  }

  gasSafetyAnswers: GasSafetyAnswer[] = [...GAS_SAFETY_ANSWERS];
  readonly GAS_NO: GasSafetyAnswer = GAS_SAFETY_ANSWERS[1];
  user: User | undefined;
  gaData: Record<string, unknown> = {};

  constructor(
    @Inject(CONFIG) readonly config: any,
    private readonly mmrService: ManageMyRepairService,
    private readonly appliancesService: AppliancesService,
    private readonly dialog: MatDialog,
    private readonly analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.user = this.mmrService.userData;
    this.setGaData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['claim']) {
      const previousPlanNumber =
        changes['claim'].previousValue?.planNumber ?? null;
      const currentPlanNumber = this.claim?.planNumber ?? null;
      const planChanged =
        changes['claim'].firstChange ||
        previousPlanNumber !== currentPlanNumber;

      if (planChanged) {
        this.resetInteractionState();
      }

      this.setupManufacturerQuestionFromClaim();

      if (!this.claim?.productTypeQuestion?.options?.length) {
        this.productTypeQuestionState = createDefaultProductTypeState(
          this.shouldDefaultToGasQuestion()
        );
      }
    }
  }

  get validWarrantyNumber(): boolean {
    return (this.planInWarrantyContactTel ?? '').length > 0;
  }

  get warrantyContactNumber(): string {
    const tel = this.planInWarrantyContactTel ?? '';
    return tel.length ? tel : this.fallbackWarrantyContactTel;
  }

  get isExcess(): boolean {
    return this.selectedOption?.claimPaymentData?.type === 'EXCESS';
  }

  get isCallOut(): boolean {
    return this.selectedOption?.claimPaymentData?.type === 'CALLOUT';
  }

  get onlyBreakDownOption(): boolean {
    if (this.currentClaimTypes?.length !== 1) {
      return false;
    }

    const [onlyOption] = this.currentClaimTypes;
    const optionValue =
      this.selectedOption?.value ??
      this.selectedOption?.claimTypeCode ??
      onlyOption?.value ??
      onlyOption?.claimTypeCode;

    return optionValue?.toUpperCase() === 'BD';
  }

  get isBreakdownSelected(): boolean {
    const value =
      this.selectedOption?.value ?? this.selectedOption?.claimTypeCode ?? '';

    return value.toUpperCase() === 'BD';
  }

  get planStillINWaitPeriodButOutOfWarranty(): boolean {
    return !this.inWarranty && this.planInWaitPeriod;
  }

  get planStillInWarrantyAndWaitPeriod(): boolean {
    return this.inWarranty && this.planInWaitPeriod;
  }

  get waitPeriodAction(): string {
    return this.claimType === 'SERVICE' ? 'a service' : 'repairs';
  }

  get canBookARepair(): boolean {
    const isInWaitPeriod =
      this.planStillINWaitPeriodButOutOfWarranty ||
      this.planStillInWarrantyAndWaitPeriod;

    if (isInWaitPeriod) {
      return false;
    }

    if (this.claimType === 'SERVICE') {
      return this.currentClaimTypes?.length > 0;
    }

    const multipleClaimOptions = this.currentClaimTypes?.length > 1;
    const inWarrantyAndOnlyAccidentalDamage =
      this.currentClaimTypes?.length === 1 &&
      this.currentClaimTypes[0]?.claimTypeCode === 'AD';
    const notInWarrantyAndOnlyBreakdownOption =
      this.onlyBreakDownOption && !this.inWarranty;

    return (
      multipleClaimOptions ||
      inWarrantyAndOnlyAccidentalDamage ||
      notInWarrantyAndOnlyBreakdownOption
    );
  }

  get title(): string {
    if (this.canBookARepair) {
      if (this.claimType === 'SERVICE') {
        return 'Your annual service';
      }

      if (this.currentClaimTypes.length > 1) {
        return 'What happened?';
      }

      return 'Need a repair?';
    }
    return 'Your repair';
  }

  get isASV(): boolean {
    return this.claimType === 'SERVICE';
  }

  get buttonText(): string {
    return this.isASV ? 'Book a service' : 'Book a repair';
  }

  // Need an additional Continue CTA on repairs pre-address confirmation one gas messaging has been viewed
  get shouldShowRepairPreAddressContinueCta(): boolean {
    return (
      this.viewModel.showGasEmergencyContent &&
      this.claimType !== 'SERVICE' &&
      !this.showAddressConfirmation
    );
  }

  navigateToMyDetails(): void {
    window.location.href = this.config.personalDetailsPage;
  }

  repairOptionSelected(option: any): void {
    const isSameOption =
      option?.claimTypeCode === this.selectedOption?.claimTypeCode;

    if (isSameOption) {
      this.selectedOption = undefined;
      this.pastFirstStage = false;
      this.addressCorrect = undefined;
    } else {
      this.addressCorrect = undefined;
      this.selectedOption = option;
    }
    this.productTypeQuestionState = createDefaultProductTypeState(
      this.shouldDefaultToGasQuestion()
    );
    this.resetProductTypeQuestionToggle = !this.resetProductTypeQuestionToggle;
    this.clearSelectedManufacturer();
  }

  onClaimTypeSelected(option: any): void {
    const isSameOption =
      option?.claimTypeCode === this.selectedOption?.claimTypeCode;

    this.repairOptionSelected(option);
    this.pastFirstStage = !isSameOption;

    this.setupManufacturerQuestionFromClaim();
  }

  bookARepair(): void {
    if (!this.selectedOption && this.currentClaimTypes.length === 1) {
      this.applySingleClaimSelection(this.currentClaimTypes[0]);
    }
    this.createClaim.emit();
    this.bookRepairClicked.emit();
  }

  downloadPlanDocs(): void {
    const planNumber = this.claim?.planNumber;

    if (!planNumber) {
      return;
    }

    const loader = this.dialog.open(LoaderComponent);
    this.appliancesService
      .searchPlanDocumentKey(planNumber)
      .pipe(
        switchMap((response: PlanDocumentKeyResponse) =>
          this.appliancesService.downloadPlanDocument(
            planNumber,
            response.result
          )
        ),
        finalize(() => loader.close())
      )
      .subscribe({
        error: () => {
          this.dialog.open(UnavailableDocumentsComponent, {
            panelClass: 'vertical-scroll',
          });
        },
      });
  }

  handleProductTypeStateChange(state: ProductTypeQuestionState): void {
    const previousState = this.productTypeQuestionState;
    this.productTypeQuestionState = state;

    const enteredOrLeftGasEmergency =
      previousState.gasSafetyAnswer !== state.gasSafetyAnswer &&
      (previousState.gasSafetyAnswer === 'YES' ||
        state.gasSafetyAnswer === 'YES');

    if (enteredOrLeftGasEmergency) {
      this.applianceInGoodCondition = undefined;
      this.receivedBaxiLetter = undefined;
      this.addressCorrect = undefined;
    }

    if (state.showGasEmergencyContent) {
      this.pastFirstStage = true;
    }

    if (
      !state.shouldAskProductTypeQuestion &&
      this.currentClaimTypes.length === 1 &&
      !this.selectedOption
    ) {
      this.applySingleClaimSelection(this.currentClaimTypes[0]);
      this.productTypeQuestionState = {
        ...this.productTypeQuestionState,
        selectedOption: state.selectedOption,
      };
      return;
    }

    const nextAddressCorrect = this.resolveAddressCorrect(state);

    if (nextAddressCorrect !== this.addressCorrect) {
      this.addressCorrect = nextAddressCorrect;
    }

    this.trackGasSafetySelection(previousState, state);
    this.trackGasEmergencyReasonSelection(previousState, state);
    this.trackGasSafetyPageView(previousState, state);
  }

  handleProductTypeSelectionChange(change: ProductTypeSelectionChange): void {
    if (change.resetClaimSelection && !this.selectedOption && !change.option) {
      this.resetInteractionState(false);
      return;
    }

    if (
      change.option &&
      this.currentClaimTypes?.length === 1 &&
      !this.selectedOption
    ) {
      this.applySingleClaimSelection(this.currentClaimTypes[0]);
      this.productTypeQuestionState = {
        ...this.productTypeQuestionState,
        selectedOption: change.option,
      };
    }

    if (change.option) {
      this.trackDamageTypeWithAppliance(change.option);
    }
  }

  handleManufacturerSelection(manufacturer: string): void {
    if (
      !manufacturer ||
      manufacturer === this.manufacturerState.selectedManufacturer
    ) {
      return;
    }

    this.manufacturerState = {
      ...this.manufacturerState,
      selectedManufacturer: manufacturer,
    };

    this.manufacturerSelected.emit(manufacturer);
  }

  handleManufacturerSearch(term: string): void {
    const normalizedTerm = this.normalizeManufacturerTerm(term);

    if (!normalizedTerm) {
      this.manufacturerState = {
        ...this.manufacturerState,
        searchOptions: [],
        selectedManufacturer: null,
        lastSearchTerm: '',
      };
      return;
    }

    if (normalizedTerm === this.manufacturerState.lastSearchTerm) {
      return;
    }

    const options = this.manufacturerState.normalizedOptions;

    const startsWithResults = options.filter((item) =>
      item.normalized.startsWith(normalizedTerm)
    );

    const results =
      startsWithResults.length > 0 || normalizedTerm.length === 1
        ? startsWithResults
        : options.filter((item) => item.normalized.includes(normalizedTerm));

    const normalizedSelected = this.normalizeManufacturerTerm(
      this.manufacturerState.selectedManufacturer
    );
    const termMatchesSelection = normalizedTerm === normalizedSelected;

    this.manufacturerState = {
      ...this.manufacturerState,
      lastSearchTerm: normalizedTerm,
      selectedManufacturer: termMatchesSelection
        ? this.manufacturerState.selectedManufacturer
        : null,
      searchOptions: results.map((item) => ({
        title: item.title,
        value: item.title,
      })),
    };
  }

  handleManufacturerChange(option: { title?: string; value?: string } | null) {
    const manufacturer = option?.value ?? option?.title ?? null;
    if (manufacturer) {
      this.handleManufacturerSelection(manufacturer);
      return;
    }

    this.clearSelectedManufacturer();
  }

  handleApplianceConditionChange(): void {
    this.receivedBaxiLetter = undefined;
    this.addressCorrect = undefined;
  }

  eventsTracked: Record<string, boolean> = {};

  handleBaxiLetterChange(): void {
    this.addressCorrect = undefined;

    const keyToTrack = this.receivedBaxiLetter
      ? 'received_letter'
      : 'not_received_letter';

    if (this.eventsTracked[keyToTrack]) {
      console.log('Already tracked event for', keyToTrack);
      return;
    }

    this.eventsTracked[keyToTrack] = true;

    posthog['brightClaims'].capture(keyToTrack, {
      product: this.mmrItem.code_description.toLowerCase(),
    });
  }

  clearSelectedManufacturer(): void {
    this.manufacturerState = {
      ...this.manufacturerState,
      selectedManufacturer: null,
      searchOptions: [],
      lastSearchTerm: '',
    };
    this.manufacturerTextSearch?.removeSelectedValue();
  }

  startSingleClaimFlow(): void {
    if (this.currentClaimTypes.length === 1 && !this.selectedOption) {
      this.applySingleClaimSelection(this.currentClaimTypes[0]);
    }

    this.addressCorrect = undefined;
    this.showAddressConfirmation = false;
    this.pastFirstStage = true;
  }

  continueToAddressConfirmation(): void {
    this.showAddressConfirmation = true;
  }

  setGaData(): void {
    this.gaData = {
      event: 'genericGAEvent',
      pagename: 'claims-what-happened',
      category: 'claims-2.0',
      clientName: 'DG Claims',
      eventCategory: 'claims what happened',
      eventAction: 'damage type',
      DigitalID: this.user?.['custom:digital_id'] || '',
      customerID: this.user?.sub || '',
      planNumber: this.claim?.planNumber || '',
      productName: `${this.claim?.item?.manufacturer || ''} ${
        this.claim?.item?.code_description || ''
      }`.toLowerCase(),
      productModel: this.claim?.item?.model_number || '',
      repairer: this.claim?.openRepair?.repairer || '',
      repairTimeline: this.claim?.openRepair?.isClaimsTrackerEnabled || '',
      claimStatus: this.claim?.openRepair?.claimStatus || '',
    };
  }

  trackClick(buttonText: string) {
    this.analyticsService.push({
      ...this.gaData,
      eventLabel: this.selectedOption?.claimTypeLabel,
      eventAction: 'damage type',
    });
    this.analyticsService.push({
      ...this.gaData,
      eventLabel: buttonText,
      eventAction: 'repair address',
    });
  }

  private applySingleClaimSelection(option: any): void {
    this.selectedOption = option;
  }

  private resetInteractionState(
    resetProductTypeQuestion: boolean = true
  ): void {
    this.selectedOption = undefined;
    this.pastFirstStage = false;
    this.applianceInGoodCondition = undefined;
    this.receivedBaxiLetter = undefined;
    this.addressCorrect = undefined;
    this.showAddressConfirmation = false;
    this.productTypeQuestionState = createDefaultProductTypeState(
      this.shouldDefaultToGasQuestion()
    );

    if (resetProductTypeQuestion) {
      this.resetProductTypeQuestionToggle =
        !this.resetProductTypeQuestionToggle;
    }
  }

  private shouldDefaultToGasQuestion(): boolean {
    return (
      !!this.claim?.isGas && !this.claim?.productTypeQuestion?.options?.length
    );
  }

  private setupManufacturerQuestionFromClaim(): void {
    const manufacturersPayload = this.claim?.manufacturers ?? [];

    const rawManufacturers =
      manufacturersPayload.length > 0 &&
      typeof manufacturersPayload[0] === 'string'
        ? (manufacturersPayload as unknown as string[])
        : manufacturersPayload.find(
            (m) => m.label?.toLowerCase() === 'make or brand'
          )?.allowedValues ?? [];

    const options = [...new Set(rawManufacturers)];
    const shouldAsk = options.length > 0;
    const currentManufacturer = this.claim?.item?.manufacturer ?? '';
    const previousSelection = this.manufacturerState.selectedManufacturer;

    let selectedManufacturer: string | null;
    if (shouldAsk) {
      selectedManufacturer =
        previousSelection && options.includes(previousSelection)
          ? previousSelection
          : null;
    } else {
      selectedManufacturer = currentManufacturer || null;
    }

    this.manufacturerState = {
      shouldAsk,
      options,
      normalizedOptions: options.map((title) => ({
        title,
        normalized: this.normalizeManufacturerTerm(title),
      })),
      searchOptions: [],
      lastSearchTerm: '',
      selectedManufacturer,
    };
  }

  private normalizeManufacturerTerm(value: string | null | undefined): string {
    return (value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  }

  private isGasSafetyBlockingState(state: ProductTypeQuestionState): boolean {
    if (!state.shouldDisplayGasQuestion) {
      return false;
    }

    return state.gasSafetyAnswer === 'YES' && !state.showGasEmergencyContent;
  }

  private resolveAddressCorrect(
    state: ProductTypeQuestionState
  ): boolean | undefined {
    if (this.isGasSafetyBlockingState(state)) {
      return undefined;
    }

    return this.addressCorrect;
  }

  private trackDamageTypeWithAppliance(applianceType: string): void {
    const claimTypeLabel = this.getClaimTypeLabel();
    if (!claimTypeLabel) {
      return;
    }

    this.analyticsService.push({
      event: 'genericGAEvent',
      eventCategory: 'claims what happened',
      eventAction: 'damage type',
      eventLabel: `${claimTypeLabel} - ${applianceType}`,
      pagename: 'claims-manage-my-repairs',
      ...this.getGasSafetyGaData(),
    });
  }

  private trackGasSafetySelection(
    previousState: ProductTypeQuestionState,
    state: ProductTypeQuestionState
  ): void {
    if (
      !state.shouldDisplayGasQuestion ||
      !state.gasSafetyAnswer ||
      previousState.gasSafetyAnswer === state.gasSafetyAnswer
    ) {
      return;
    }

    const applianceType = this.getApplianceType(state);

    this.analyticsService.push({
      event: 'genericGAEvent',
      eventCategory: 'claims what happened',
      eventAction: 'damage type',
      eventLabel: `${applianceType} - smell gas ${state.gasSafetyAnswer.toLowerCase()}`,
      pagename: 'claims-manage-my-repairs',
      ...this.getGasSafetyGaData(),
    });
  }

  private trackGasEmergencyReasonSelection(
    previousState: ProductTypeQuestionState,
    state: ProductTypeQuestionState
  ): void {
    if (
      state.gasSafetyAnswer !== 'YES' ||
      !state.gasEmergencyReason ||
      previousState.gasEmergencyReason === state.gasEmergencyReason
    ) {
      return;
    }

    this.analyticsService.push({
      event: 'genericGAEvent',
      eventCategory: 'claims what happened',
      eventAction: 'smell gas - yes',
      eventLabel: this.getGasEmergencyReasonLabel(state.gasEmergencyReason),
      pagename: 'claims-manage-my-repairs',
      ...this.getGasSafetyGaData(),
    });
  }

  private trackGasSafetyPageView(
    previousState: ProductTypeQuestionState,
    state: ProductTypeQuestionState
  ): void {
    if (
      !state.showGasEmergencyContent ||
      previousState.showGasEmergencyContent
    ) {
      return;
    }

    this.analyticsService.push({
      event: 'spa-pageview',
      pagename: 'claims-manage-my-repairs-gas-safety',
      ...this.getGasSafetyGaData(),
    });
  }

  private readonly claimTypeGaLabels: Record<string, string> = {
    BD: 'It broke down',
    AD: 'It got damaged by accident',
  };

  private getClaimTypeLabel(): string {
    const option = this.selectedOption ?? this.currentClaimTypes?.[0];

    if (!option) {
      return '';
    }

    return (
      this.claimTypeGaLabels[option.claimTypeCode] ??
      option.claimTypeLabel ??
      ''
    );
  }

  private getApplianceType(state: ProductTypeQuestionState): string {
    return (
      state.selectedOption || this.productTypeQuestionState.selectedOption || ''
    );
  }

  private getGasEmergencyReasonLabel(reason: string): string {
    return (
      GAS_EMERGENCY_REASONS.find((r) => r.value === reason)?.label ?? reason
    );
  }

  private getGasSafetyGaData(): Record<string, unknown> {
    return {
      category: 'claims-2.0',
      customerID: this.user?.sub || '',
      DigitalID: this.user?.['custom:digital_id'] || '',
      planNumber: this.claim?.planNumber || '',
      productName: `${this.claim?.item?.manufacturer || ''} ${
        this.claim?.item?.code_description || ''
      }`.toLowerCase(),
      productsStatus: this.claim?.planState?.toLowerCase() || '',
      productModel: this.claim?.item?.model_number || '',
      repairTimeline: this.claim?.openRepair?.isClaimsTrackerEnabled || '',
      repairer: this.claim?.openRepair?.repairer || '',
    };
  }
}
